import React, { useMemo } from "react";
import { SurveySegment, getSegmentListKey2, segmentListIsInLists } from "../../../amorg-common";
import { Button } from "../../../designSystem/button/Button";
import { Flex } from "../../../designSystem/compositeComponents/FlexHelpers";
import { OnOff } from "../../../designSystem/inputs/OnOff";
import { TextInput } from "../../../designSystem/inputs/TextInput";
import { Span14 } from "../../../designSystem/typography/Body";
import { SegmentationViewMode, amorgLocalStorage } from "../../../utils/amorgLocalStorage";
import { SegmentListViewData, SegmentListView } from "../../workplaceassessments/[activityId]/_components/tabs/_components/SegmentListView";
import { Divider } from "../../../designSystem/Divider";

type EditableSegmentPanelProps = {
    segmentLists: {
        segments: SurveySegment[],
        answerCount: number;
    }[],
    selection: SurveySegment[][],
    disableEdit?: boolean,
    noSelectionContent?: React.ReactNode,
    displayAnswerCount?: boolean,
    staticView?:SegmentationViewMode,
    localStorageViewModeKey?: string,
    onSelectionChange: (selectedSegmentLists: SurveySegment[][]) => void;
};

export function EditableSegmentPanel (props: EditableSegmentPanelProps): JSX.Element 
{
    const [view, setView] = React.useState<SegmentationViewMode>('pills');
    const [listData, setListData] = React.useState<SegmentListViewData[]>([]);
    const [searchText, setSearchText] = React.useState('');

    React.useEffect(() =>
    {
        if (props.staticView)
        {
            setView(props.staticView);
        }
        else if(props.localStorageViewModeKey)
        {
            const storedView = amorgLocalStorage.surveySegmentation.viewMode.readOrCreate(props.localStorageViewModeKey);
            setView(storedView);
        }
        // use default

    }, [props.staticView]);  

    React.useEffect(()=> {

        const newViewData = props.segmentLists.map(sl => ({
            segments: sl.segments,
            identifier: getSegmentListKey2(sl.segments),
            selected: segmentListIsInLists(props.selection, sl.segments),
            answerCount: sl.answerCount
        }));

        setListData(newViewData);
    }, [props.segmentLists, props.selection]);

    const shownSegmentLists = useMemo(() =>
    {
        let filtered = listData;
        const showAll = true;
        if (!showAll)
        {
            filtered = listData.filter(sl => sl.selected);
        }

        if (searchText.trim().length > 0)
        {
            return filtered.filter(sl =>
                sl.segments.some(s =>
                    s.name.toLowerCase().includes(searchText.toLowerCase())
                    || s.value.toLowerCase().includes(searchText.toLowerCase())
                )
            );
        }

        return filtered;
    }, [listData, searchText]);

    function handleClickToggleAll()
    {
        const visibleSegmentLists = shownSegmentLists.map(s => s.segments);
        const hiddenSegementLists = listData.filter(ld => segmentListIsInLists(visibleSegmentLists, ld.segments) === false);
        const hiddenSelections = hiddenSegementLists.filter(ns => ns.selected).map(ns => ns.segments);

        const newSelection = shownSegmentLists.every(s => s.selected)
            ? hiddenSelections
            : [...hiddenSelections, ...shownSegmentLists.map(sl => sl.segments)];

        props.onSelectionChange(newSelection);
    }

    function handleSelectionChanged (identifier: string, selected: boolean)
    {
        const match = listData.find(sl => getSegmentListKey2(sl.segments) === identifier);
        if (match)
        {
            if(selected)
            {
                props.onSelectionChange(
                    [
                        ...listData.filter(ld => ld.selected).map(ld => ld.segments),
                        match.segments
                    ]
                );
            }
            else {
                props.onSelectionChange(
                    listData.filter(ld => ld.selected && getSegmentListKey2(ld.segments) !== identifier).map(ld => ld.segments)
                );
            }
        }
    }
    
    function handleViewModeChange (e: React.MouseEvent<HTMLButtonElement>, mode: SegmentationViewMode)
    {
        setView(mode);
        if(props.localStorageViewModeKey)
            amorgLocalStorage.surveySegmentation.viewMode.write(props.localStorageViewModeKey, mode);
        e.stopPropagation();
    }

    function handleSearchTextChange (text: string)
    {
        setSearchText(text);
    }

    return (
        <Flex flexDirection="column" gap={16}>

            <Flex justifyContent="space-between" alignItems="center" >
                <Flex justifyContent="space-between" style={{ height: '26px' }}>
                    <Flex alignItems="center" gap={16}>
                        <Flex alignItems="center" gap={4}>
                            <OnOff size="small" active={shownSegmentLists.every(s => s.selected)} onClick={handleClickToggleAll} />
                            <Span14>Vælg alle</Span14>
                        </Flex>
                    </Flex>
                    
                </Flex>
                
                <Flex justifyContent="flex-end" alignItems="center" gap={4} style={{ flexGrow: 1 }}>

                    <TextInput placeholder="Søg..."
                        verticalPaddingOverride={2}
                        size="32px"
                        value={searchText}
                        onChange={handleSearchTextChange}
                        trailingIcon="interface_actions_search"
                    />

                    {props.staticView === undefined &&
                        <Button variant={view === 'pills' ? 'text' : 'clean'} 
                            size="32px"
                            icon='table' 
                            toolTipText={ view === 'pills' ? "Vis som tabel" : "Vis som piller"} 
                            onClick={(e) => handleViewModeChange(e, view==='pills' ? 'table' : 'pills')} />
                    }
                </Flex>
            </Flex>

            <Divider />

            <SegmentListView
                view={view}
                segmentLists={shownSegmentLists}
                onToggleSelect={props.disableEdit || !props.onSelectionChange
                    ? undefined
                    : handleSelectionChanged
                }
                onToggleAll={props.disableEdit || !props.onSelectionChange
                    ? undefined
                    : handleClickToggleAll
                }
                displayAnswerCount={props.displayAnswerCount}
                noSelectionContent={props.noSelectionContent}
                localStorageViewModeKey={props.localStorageViewModeKey}
            />
            
        </Flex>
    );
}